import { useState } from "react";

export interface RangeValue {
  start: Date | null;
  end: Date | null;
}

export type CalendarMode = "single" | "range";

export function useCalendar(
  value: RangeValue | Date | null | undefined,
  onChange?: (v: RangeValue | Date | null) => void,
  mode: CalendarMode = "range",
  hasTime: boolean = false,
  hasPeriodPicker: boolean = false,
) {
  const safeRange: RangeValue =
    mode === "range"
      ? ((value as RangeValue) ?? { start: null, end: null })
      : { start: (value as Date | null) ?? null, end: null };

  const initialMonth = safeRange.start ?? new Date();

  const [currentMonth, setCurrentMonth] = useState<Date>(initialMonth);
  const [view, setView] = useState<"day" | "month" | "year" | "period">(hasPeriodPicker ? "period" : "day");
  const [isInFreeRange, setIsInFreeRange] = useState(false);
  const [freeRangeStart, setFreeRangeStart] = useState<Date | null>(null);

  function next() {
    const d = new Date(currentMonth);
    if (view === "day") d.setMonth(d.getMonth() + 1);
    else if (view === "month" || view === "period") d.setFullYear(d.getFullYear() + 1);
    else d.setFullYear(d.getFullYear() + 12);
    setCurrentMonth(d);
  }

  function prev() {
    const d = new Date(currentMonth);
    if (view === "day") d.setMonth(d.getMonth() - 1);
    else if (view === "month" || view === "period") d.setFullYear(d.getFullYear() - 1);
    else d.setFullYear(d.getFullYear() - 12);
    setCurrentMonth(d);
  }

  function goYearMode() {
    setView((prev) => {
      if (prev === "period") return "year";
      if (prev === "day") return "month";
      if (prev === "month") return "year";
      if (isInFreeRange) return "day";
      return hasPeriodPicker ? "period" : "day";
    });
  }

  function selectMonth(month: number) {
    const d = new Date(currentMonth);
    d.setMonth(month);
    setCurrentMonth(d);
    setView("day");
  }

  function selectYear(year: number) {
    const d = new Date(currentMonth);
    d.setFullYear(year);
    setCurrentMonth(d);
    if (hasPeriodPicker && !isInFreeRange) {
      setView("period");
    } else {
      setView("month");
    }
  }

  function selectDate(date: Date) {
    function preserveTime(newDate: Date, existingDate: Date | null) {
      if (hasTime) {
        if (existingDate) {
          newDate.setHours(existingDate.getHours(), existingDate.getMinutes(), 0, 0);
        }
        return newDate;
      }

      newDate.setHours(12, 0, 0, 0);
      return newDate;
    }

    if (mode === "single") {
      const dateWithTime = preserveTime(new Date(date), safeRange.start);
      onChange?.(dateWithTime);
      return;
    }

    if (isInFreeRange) {
      if (!freeRangeStart) {
        const dateWithTime = preserveTime(new Date(date), null);
        setFreeRangeStart(dateWithTime);
        onChange?.({ start: dateWithTime, end: null });
      } else {
        const picked = preserveTime(new Date(date), null);
        if (picked < freeRangeStart) {
          onChange?.({ start: picked, end: freeRangeStart });
        } else {
          onChange?.({ start: freeRangeStart, end: picked });
        }
        setFreeRangeStart(null);
      }
      return;
    }

    const { start, end } = safeRange;

    if (!start) {
      const dateWithTime = preserveTime(new Date(date), null);
      onChange?.({ start: dateWithTime, end: null });
      return;
    }

    if (start && !end) {
      if (date < start) {
        const newStart = preserveTime(new Date(date), null);
        const newEnd = preserveTime(new Date(start), start);
        onChange?.({ start: newStart, end: newEnd });
      } else {
        const newEnd = preserveTime(new Date(date), null);
        onChange?.({ start, end: newEnd });
      }
      return;
    }

    const dateWithTime = preserveTime(new Date(date), null);
    onChange?.({ start: dateWithTime, end: null });
  }

  function selectPeriodMonth(month: number, year: number) {
    const start = new Date(year, month, 1, 12, 0, 0, 0);
    const end = new Date(year, month + 1, 0, 12, 0, 0, 0);
    onChange?.({ start, end });
  }

  function selectQuarter(quarter: number, year: number) {
    const startMonth = (quarter - 1) * 3;
    const start = new Date(year, startMonth, 1, 12, 0, 0, 0);
    const end = new Date(year, startMonth + 3, 0, 12, 0, 0, 0);
    onChange?.({ start, end });
  }

  function goToFreeRange() {
    setView("day");
    setIsInFreeRange(true);
    setFreeRangeStart(null);
  }

  function goToPeriodView() {
    setView("period");
    setIsInFreeRange(false);
    setFreeRangeStart(null);
  }

  function reset(value: RangeValue | Date | null) {
    if (mode === "single") {
      setCurrentMonth((value as Date | null) ?? new Date());
    } else {
      const v = value as RangeValue;
      setCurrentMonth(v.start ?? new Date());
    }
    setView(hasPeriodPicker ? "period" : "day");
    setIsInFreeRange(false);
    setFreeRangeStart(null);
  }

  return {
    view,
    currentMonth,
    next,
    prev,
    goYearMode,
    selectMonth,
    selectYear,
    selectDate,
    reset,
    selectPeriodMonth,
    selectQuarter,
    goToFreeRange,
    goToPeriodView,
    isInFreeRange,
  };
}
